import { BaseFilter } from './BaseFilter';
import type { T2DPipelineState, TWebGLPipelineState } from './typedefs';
import { isWebGLPipelineState } from './utils';
import { classRegistry } from '../ClassRegistry';

type ComposedOwnProps = {
  subFilters: BaseFilter<string, Record<string, unknown>, Record<string, unknown>>[];
};

type ComposedSerializedProps = {
  subFilters: Record<string, unknown>[];
};

/**
 * A container class that knows how to apply a sequence of filters to an input image.
 */
export class Composed extends BaseFilter<
  'Composed',
  ComposedOwnProps,
  ComposedSerializedProps
> {
  /**
   * A non sparse array of filters to apply
   */
  declare subFilters: ComposedOwnProps['subFilters'];

  static override type = 'Composed';

  constructor(
    options: { subFilters?: BaseFilter<string>[] } & Record<string, unknown> = {},
  ) {
    super(options);
    this.subFilters = options.subFilters || [];
  }

  /**
   * Apply this container's filters to the input image provided.
   *
   * @param {Object} options
   * @param {Number} options.passes The number of filters remaining to be applied.
   */
  override applyTo(options: TWebGLPipelineState | T2DPipelineState) {
    if (isWebGLPipelineState(options)) {
      options.passes += this.subFilters.length - 1;
    }
    this.subFilters.forEach((filter) => {
      filter.applyTo(options);
    });
  }

  /**
   * Serialize this filter into JSON.
   * @returns {Object} A JSON representation of this filter.
   */
  override toObject() {
    return {
      type: this.type,
      subFilters: this.subFilters.map((filter) => filter.toObject()),
    };
  }

  override isNeutralState() {
    return !this.subFilters.some((filter) => !filter.isNeutralState());
  }

  /**
   * Deserialize a JSON definition of a ComposedFilter into a concrete instance.
   * @param {oject} object Object to create an instance from
   * @param {object} [options]
   * @param {AbortSignal} [options.signal] handle aborting `BlendImage` filter loading, see https://developer.mozilla.org/en-US/docs/Web/API/AbortController/signal
   * @returns {Promise<Composed>}
   */
  static override fromObject(
    object: Record<string, unknown>,
    options: { signal: AbortSignal } = {
      signal: new AbortController().signal,
    },
  ): Promise<Composed> {
    const serializedFilters = (object['subFilters'] ?? []) as Record<
      string,
      unknown
    >[];

    return Promise.all(
      serializedFilters.map((filterConfig) => {
        const filterType = String(filterConfig['type'] ?? '');
        const filterClass = classRegistry.getClass<typeof BaseFilter>(
          filterType,
        );
        if (!filterClass.fromObject) {
          throw new Error(`Filter class "${filterType}" does not support fromObject`);
        }
        return filterClass.fromObject(filterConfig, options) as Promise<
          BaseFilter<string, Record<string, unknown>, Record<string, unknown>>
        >;
      }),
    ).then((enlivedFilters) => new this({ subFilters: enlivedFilters }));
  }
}

classRegistry.setClass(Composed);
